import styled from 'styled-components';
import { Link } from '@/components/Link';
import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import type { PurchaseActionType } from '@/types';
import { PurchaseAction } from '@/constants';

const getBackgroundColor = (purchaseAction: PurchaseActionType) => {
  if (purchaseAction === PurchaseAction.BackOrder) {
    return 'var(--blue-light-300)';
  }

  if (purchaseAction === PurchaseAction.Notify) {
    return 'var(--purple-600)';
  }

  if (purchaseAction === PurchaseAction.PreOrder) {
    return 'var(--pink-700)';
  }

  return 'var(--brand-500)';
};

const getForegroundColor = (purchaseAction: PurchaseActionType) => {
  if (
    purchaseAction === PurchaseAction.Notify ||
    purchaseAction === PurchaseAction.PreOrder
  ) {
    return 'var(--white)';
  }

  return 'var(--black)';
};

const getHoverStyles = (purchaseAction: PurchaseActionType) => {
  if (purchaseAction === PurchaseAction.BackOrder) {
    return `
      background: var(--blue-light-400);
      box-shadow: 0 0 0 4px var(--blue-light-100), 0 1px 2px 0 rgb(10 13 18 / 0.05);
    `;
  }

  if (purchaseAction === PurchaseAction.Notify) {
    return `
      background: var(--purple-700);
      box-shadow: 0 0 0 4px var(--purple-100), 0 1px 2px 0 rgb(10 13 18 / 0.05);
    `;
  }

  if (purchaseAction === PurchaseAction.PreOrder) {
    return `
      background: var(--pink-800);
      box-shadow: 0 0 0 4px var(--pink-100), 0 1px 2px 0 rgb(10 13 18 / 0.05);
    `;
  }

  return `
    background: var(--brand-600);
    box-shadow: 0 0 0 4px var(--brand-100), 0 1px 2px 0 rgb(10 13 18 / 0.05);
  `;
};

export const Tile = styled.div.attrs({ className: 'product-tile' })`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  max-width: 198px;
  min-width: 0;
  box-sizing: border-box;
  height: 100%;

  @media (width >= 48rem) {
    width: 230px;
  }
`;

export const ImageContainer = styled.div<{ $isBook: boolean }>`
  height: 240px;
  padding: 12px 16px;
  border-radius: var(--radius-large);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  container-type: inline-size;
  background: ${props => (props.$isBook ? 'var(--grey-100)' : 'var(--white)')};
  border: 1px solid var(--grey-200);

  .surface-chalk-grey &,
  .surface-pebble-grey &,
  .surface-zest-green &,
  .surface-moss-green &,
  .surface-pale-green &,
  .surface-light-sage &,
  .surface-sage &,
  .surface-grey &,
  .surface-brand-200 &,
  .surface-brand-250 & {
    background: var(--white);
  }
`;

export const ImageLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  &:focus-visible {
    box-shadow: var(--shadow-focus);
    border-radius: var(--radius-base);
    outline: 0;
  }
`;

export const ProductImage = styled.img<{ $isBook: boolean }>`
  max-height: 100%;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  box-shadow: ${props => (props.$isBook ? '0 4px 14px rgb(0 0 0 / 18%)' : 'none')};
`;

export const BadgeWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 64px;
  height: 64px;

  & img {
    width: 100%;
    height: 100%;
  }
`;

export const ContentWrap = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

export const MetaLink = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: auto;
  text-decoration: none;
  color: var(--black);

  &[data-hovered] {
    text-decoration: none;
  }

  &[data-hovered] > * {
    color: var(--grey-600);
    cursor: pointer;
    text-decoration: none;
  }

  &[data-hovered] > :first-child {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .surface-moss-green &[data-hovered] > * {
    color: var(--white);
  }

  &:focus-visible {
    box-shadow: var(--shadow-focus);
    border-radius: var(--radius-base);
    outline: 0;
  }
`;

export const Title = styled.div`
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: var(--black);
  min-height: 40px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  .surface-moss-green & {
    color: var(--white);
  }
`;

export const Author = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: var(--grey-600);
  min-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .surface-zest-green & {
    color: var(--grey-800);
  }

  .surface-moss-green & {
    color: var(--grey-200);
  }
`;

export const Price = styled.div`
  font-size: 17px;
  font-weight: 700;
  line-height: 22px;
  color: var(--black);
  margin-top: 4px;
  min-height: 22px;
  display: inline-flex;
  align-items: baseline;

  .surface-moss-green & {
    color: var(--white);
  }
`;

export const OldPrice = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: var(--grey-500);
  text-decoration: line-through;
  text-decoration-thickness: 1px;
  margin-left: 8px;

  .surface-zest-green & {
    color: var(--grey-700);
  }

  .surface-moss-green & {
    color: var(--grey-300);
  }
`;

export const CtaButton = styled(Button)<{
  $purchaseAction: PurchaseActionType;
}>`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 8px 0 0;
  border-radius: var(--radius-large);
  background: ${props => getBackgroundColor(props.$purchaseAction)};
  border: none;
  box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  color: ${props => getForegroundColor(props.$purchaseAction)};

  &[data-hovered] {
    ${props => getHoverStyles(props.$purchaseAction)}
  }

  & svg {
    width: 18px;
    height: 18px;

    & path {
      stroke: ${props => getForegroundColor(props.$purchaseAction)};
    }
  }

  .surface-zest-green & {
    background: var(--brand-700);
    color: var(--white);

    & svg path {
      stroke: var(--white);
    }
  }

`;

export const PromoChip = styled.div<{ $purchaseAction?: PurchaseActionType; $isEmpty?: boolean }>`
  display: flex;
  align-items: flex-start;
  column-gap: 8px;
  padding: ${props => (props.$isEmpty ? '0' : '4px 8px')};
  background: transparent;
  border-radius: 0;
  font-size: 11.5px;
  font-weight: 600;
  color: ${props => {
    if (props.$purchaseAction === PurchaseAction.BackOrder) return 'var(--blue-light-800)';
    if (props.$purchaseAction === PurchaseAction.PreOrder) return 'var(--pink-700)';
    return 'var(--brand-800)';
  }};
  line-height: 14px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  margin-top: auto;
  padding-top: var(--spacing-small);
  min-height: 41px;

  & svg {
    flex: 0 0 auto;
    width: 12px;
    height: 12px;
    margin-top: 1px;

    & path {
      stroke: ${props => {
        if (props.$purchaseAction === PurchaseAction.BackOrder) return 'var(--blue-light-800)';
        if (props.$purchaseAction === PurchaseAction.PreOrder) return 'var(--pink-700)';
        return 'var(--brand-900)';
      }};
    }
  }

  & > span {
    flex: 1 1 auto;
    min-width: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .surface-zest-green & {
    color: var(--brand-900);
  }

  .surface-moss-green & {
    color: var(--brand-200);

    & svg path {
      stroke: var(--brand-200);
    }
  }
`;

export const NotifyTrigger = styled(IconButton)<{
  $purchaseAction: PurchaseActionType;
}>`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 8px 0 0;
  border-radius: var(--radius-large);
  background: var(--purple-600);
  border: none;
  box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  color: var(--white);

  &[data-hovered] {
    background: var(--purple-700);
    box-shadow: 0 0 0 4px var(--purple-100), 0 1px 2px 0 rgb(10 13 18 / 0.05);
  }

  & svg {
    width: 18px;
    height: 18px;

    & path {
      stroke: var(--white);
    }
  }

  & span {
    display: flex;
    align-items: center;
    gap: var(--spacing-small);
  }
`;
